import { Injectable, inject } from '@angular/core';
import { DataService } from '../services/data.service';
import * as pasBundleGenerator from '@simpatico.ai/lib-pas-request/src/pas-bundle-generator.js';
import type { fhirR4 } from '@smile-cdr/fhirts';
import { Observable, from } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import { EndpointService } from '../services/endpoint.service';
import { ResourceType } from '../models/ResourceType';

// need wrapper for testing/mocking
export abstract class PasBundleGeneratorWrapper {
  static readonly generatePasRequestBundle =
    pasBundleGenerator.generatePasRequestBundle;
  static readonly generateSubmitAttachmentParameters =
    pasBundleGenerator.generateSubmitAttachmentParameters;
}
@Injectable({
  providedIn: 'root',
})
export class SubmitService {
  private readonly dataService = inject(DataService);
  private readonly endpointService = inject(EndpointService);

  readonly signatureExtensionUrl =
    'http://hl7.org/fhir/StructureDefinition/questionnaireresponse-signature';
  readonly signatureTypeCoding = {
    system: 'urn:iso-astm:E1762-95:2013',
    code: '1.2.840.10065.1.12.1.5',
  };
  readonly contextExtensionUrl =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/qr-context';

  signAndSaveQr(
    qr: fhirR4.QuestionnaireResponse,
    name: string,
  ): Observable<fhirR4.QuestionnaireResponse> {
    const sign = this.createSignature(name);
    const questionnaireResponse = cloneDeep(qr);

    questionnaireResponse.status = 'completed';
    const signExt = questionnaireResponse.extension?.find(
      (ext) => ext.url === this.signatureExtensionUrl,
    );
    if (signExt) {
      signExt.valueSignature = sign.valueSignature;
    } else {
      questionnaireResponse.extension = (
        questionnaireResponse.extension || []
      ).concat(sign);
    }
    return from(
      this.dataService.updateResource(
        questionnaireResponse,
      ) as Promise<fhirR4.QuestionnaireResponse>,
    );
  }

  private createSignature(name: string) {
    return {
      url: this.signatureExtensionUrl,
      valueSignature: {
        type: [this.signatureTypeCoding],
        when: new Date().toJSON(),
        who: {
          reference: this.dataService.fhirUser,
        },
        data: btoa(name),
      },
    };
  }

  async createPasBundle(questionnaireResponse: fhirR4.QuestionnaireResponse) {
    const order = questionnaireResponse.extension
      ?.filter((ext) => ext.url === this.contextExtensionUrl)
      .map((ext) => ext.valueReference?.reference)
      .find(
        (ref) =>
          ref &&
          (ref.startsWith('ServiceRequest') ||
            ref.startsWith('DeviceRequest') ||
            ref.startsWith('NutritionOrder') ||
            ref.startsWith('MedicationRequest') ||
            ref.startsWith('CommunicationRequest') ||
            ref.startsWith('VisionPrescription')),
      );
    const bundle = await PasBundleGeneratorWrapper.generatePasRequestBundle(
      order,
      await this.dataService.getServerUrl(),
      await this.dataService.getAuth(),
    );
    if (bundle.warnings?.length > 0)
      console.log('Warning:\n', bundle.warnings.join('\n '));
    if (bundle.success) return bundle.bundle;
    throw bundle.messageLog;
  }

  async submitPasBundle(payload: fhirR4.Bundle | fhirR4.Parameters) {
    return this.dataService.post<fhirR4.Bundle | fhirR4.OperationOutcome>(
      payload,
      this.endpointService.getPasSubmitUrl(),
    );
  }

  async submitClaimResponse(response: fhirR4.Bundle | fhirR4.OperationOutcome) {
    const claimResponse = this.extractClaimResponse(response);
    if (claimResponse) await this.dataService.createResource(claimResponse);
  }

  private extractClaimResponse(
    response: fhirR4.Bundle | fhirR4.OperationOutcome,
  ): fhirR4.ClaimResponse | undefined {
    return (response as fhirR4.Bundle).entry?.find(
      (entry) => entry.resource?.resourceType === ResourceType.ClaimResponse,
    )?.resource as fhirR4.ClaimResponse | undefined;
  }

  async submitAttachment(task: fhirR4.Task) {
    const params =
      await PasBundleGeneratorWrapper.generateSubmitAttachmentParameters(
        task,
        await this.dataService.getServerUrl(),
        await this.dataService.getAuth(),
      );
    if (params.created) {
      await this.dataService.post(
        params.parameters,
        this.endpointService.getCdexAttachmentUrl(),
      );
      await this.setTaskStatusToCompleted(task);
    } else throw new Error(params.messageLog);
  }

  async setTaskStatusToCompleted(task: fhirR4.Task) {
    const taskResource = cloneDeep(task);
    taskResource.status = 'completed';
    await this.dataService.updateResource(taskResource);
  }
}
